import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import { IconCloudUpload, IconFileExcel } from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const EXCEL_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
} as const;

type TableExcelImportModalProps = {
  opened: boolean;
  onClose: () => void;
  onImport: () => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  errors?: string[];
  notification?: { message: string; color: string } | null;
  hint?: string;
  showImportRules?: boolean;
};

export function TableExcelImportModal({
  opened,
  onClose,
  onImport,
  file,
  onFileChange,
  errors = [],
  notification = null,
  hint,
  showImportRules = true,
}: TableExcelImportModalProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      if (files[0]) {
        onFileChange(files[0]);
      }
    },
    accept: EXCEL_ACCEPT,
    maxSize: 30 * 1024 * 1024,
    maxFiles: 1,
  });

  const resetAndClose = () => {
    onFileChange(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      centered
      title={t('common.import.title')}
    >
      <Stack spacing="md">
        <Stack spacing="xs">
          <Text size="sm" color="dimmed">
            {hint ?? t('common.import.description')}
          </Text>
          {showImportRules ? (
            <Stack spacing={4}>
              <Text size="sm" weight={500}>
                {t('common.import.rulesTitle')}
              </Text>
              <Text size="sm" color="dimmed" component="ul" style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>{t('common.import.ruleInsert')}</li>
                <li>{t('common.import.ruleUpdate')}</li>
                <li>{t('common.import.ruleDelete')}</li>
              </Text>
            </Stack>
          ) : null}
        </Stack>
        <div
          {...getRootProps()}
          style={{
            padding: '1rem',
            border: '1px dashed #ccc',
            textAlign: 'center',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} />
          {file ? (
            <Group style={{ flexDirection: 'column' }} spacing="xs">
              <IconFileExcel size={50} color={theme.colors.green[6]} stroke={1.5} />
              <Text align="center" weight={700} size="lg">
                {file.name}
              </Text>
            </Group>
          ) : (
            <>
              {isDragActive ? (
                <IconCloudUpload size={50} color={theme.colors.blue[6]} stroke={1.5} />
              ) : (
                <IconCloudUpload size={50} stroke={1.5} />
              )}
              <Text align="center" weight={700} size="lg" mt="xl">
                {t('common.dragDropUpload')}
              </Text>
              <Text align="center" size="sm" mt="xs" color="dimmed">
                {t('common.excelOnlyHint')}
              </Text>
            </>
          )}
        </div>
        {errors.length > 0 && (
          <Stack spacing="xs">
            {errors.map((error, index) => (
              <Text key={index} color="red" size="sm">
                {error}
              </Text>
            ))}
          </Stack>
        )}
        {notification?.message ? (
          <Text color={notification.color} align="center" size="sm">
            {notification.message}
          </Text>
        ) : null}
        <Group position="right" spacing="xs">
          <Button variant="default" onClick={resetAndClose}>
            {t('common.cancel')}
          </Button>
          <Button size="xs" onClick={onImport} disabled={!file}>
            {t('common.import.excel')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
